import React, { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useMutation } from '@apollo/client';

import { useInstoreEnrollment } from 'src/hooks/use-instore-enrollment';
import { SmallGreenQrCode } from 'src/assets/small-green-qr-code';
import { SmallGreenPhoneTap } from 'src/assets/small-green-phone-tap';
import { SmallGreenDiamond } from 'src/assets/small-green-diamond';
import { InstoreEnrollmentStatus } from 'src/state/instore-enrollment';
import { CreatePointsBoostDocument, UpdateProfileDocument } from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLoyalty } from '../loyalty/use-loyalty';
import { useInstoreEnrollmentAnalytics } from '../hooks/use-instore-enrollment-analytics';

import {
  MainContainer,
  ContentContainer,
  HeadingContainer,
  Heading,
  FooterContainer,
  Image,
  ImageContainer,
  TextContentContainer,
  TextContentHeading,
  GreenText,
  LineItemContainer,
  LineItem,
  LineItemText,
  BackButton,
  OuterContainer,
  CloseText,
} from './success.styles';

export function Success(): JSX.Element {
  const instoreEnrollment = useInstoreEnrollment();
  const declinedPayByBank = useObserver(() => instoreEnrollment.declinedPayByBank);
  const isFullyEnrolled = useObserver(() => instoreEnrollment.fullyEnrolled);
  const user = useUser();
  const { dispensary } = useDispensary();
  const [updateProfile] = useMutation(UpdateProfileDocument);
  const [createPointsBoost] = useMutation(CreatePointsBoostDocument);
  const { pointsBoostValue, rewardsProgram } = useLoyalty();
  const flags = useFlags();
  const pointsBoostEnabled = flags['growth.ecomm.loyalty-and-pbb-points-boost'];
  const { trackStepViewed, logError } = useInstoreEnrollmentAnalytics();

  // Track success page viewed on mount
  useEffect(() => {
    trackStepViewed('success');
  }, [trackStepViewed]);

  useEffect(() => {
    if (isFullyEnrolled && !user.profile?.inStoreEnrollmentCompleted && pointsBoostEnabled) {
      void createPointsBoost({
        variables: {
          input: {
            dispensaryId: dispensary.id,
            signedUpForLoyalty: true,
            signedUpForPbb: !declinedPayByBank,
            userId: user.id,
            program: rewardsProgram,
          },
        },
      }).catch((error: unknown) => {
        logError('Failed to create points boost on enrollment success', {
          error: error instanceof Error ? error.message : String(error),
          userId: user.id,
        });
      });

      void updateProfile({
        variables: {
          profile: {
            _id: user.id,
            firstName: user.profile?.firstName,
            lastName: user.profile?.lastName,
            email: user.profile?.email,
            phone: user.profile?.phone,
            birthday: user.profile?.birthday,
            emailNotifications: user.profile?.emailNotifications,
            emailOptIn: user.profile?.emailOptIn,
            textNotifications: user.profile?.textNotifications,
            photoId: user.profile?.photoId,
            inStoreEnrollmentCompleted: true,
          },
        },
      }).catch((error: unknown) => {
        logError('Failed to update profile on enrollment success', {
          error: error instanceof Error ? error.message : String(error),
          userId: user.id,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullyEnrolled, updateProfile, user.id, user.profile]);

  const imageSource = declinedPayByBank
    ? 'https://images.dutchie.com/pay/loyalty-only-enrollment-success.png'
    : 'https://images.dutchie.com/pay/pay-by-bank-enrollment-success.png';

  const handleGoBackToPayByBankEnrollment = (): void => {
    instoreEnrollment.setCurrentStatus(InstoreEnrollmentStatus.payByBankEnrollment);
  };

  return (
    <OuterContainer data-testid='instore-enrollment-success-page'>
      <MainContainer>
        <HeadingContainer>
          <Heading size='large' tag='h1'>
            {declinedPayByBank ? (
              <>
                You can now earn points with
                <br />
                every purchase!
              </>
            ) : (
              <>
                {isFullyEnrolled && user.profile?.inStoreEnrollmentCompleted
                  ? "You're already earning points!"
                  : "You're all set!"}
              </>
            )}
          </Heading>
          <CloseText size='small' tag='h2'>
            Feel free to close this window.
          </CloseText>
        </HeadingContainer>
        <ContentContainer>
          <ImageContainer>
            <Image
              src={imageSource}
              alt={declinedPayByBank ? 'loyalty only enrollment success' : 'pay by bank enrollment success'}
            />
          </ImageContainer>
          {!declinedPayByBank && (
            <TextContentContainer>
              <TextContentHeading size='large' tag='h2'>
                Ask to <GreenText>Pay by Bank</GreenText> <br /> at the register
              </TextContentHeading>
              <LineItemContainer>
                <LineItem>
                  <SmallGreenQrCode />
                  <LineItemText size='small' tag='p'>
                    Scan QR code at checkout
                  </LineItemText>
                </LineItem>
                <LineItem>
                  <SmallGreenPhoneTap />
                  <LineItemText size='small' tag='p'>
                    Confirm payment
                  </LineItemText>
                </LineItem>
                <LineItem>
                  <SmallGreenDiamond />
                  <LineItemText size='small' tag='p'>
                    Earn points & rewards
                  </LineItemText>
                </LineItem>
              </LineItemContainer>
            </TextContentContainer>
          )}
        </ContentContainer>
        <FooterContainer>
          {declinedPayByBank && (
            <BackButton size='large' onClick={handleGoBackToPayByBankEnrollment}>
              Get {pointsBoostValue} bonus points!
            </BackButton>
          )}
        </FooterContainer>
      </MainContainer>
    </OuterContainer>
  );
}
